/*
  Поле «Судебный участок»: выбор из заведённых участков И ручной ввод.

  🔴 Решение владельца 14.09.2026. До него участок вводился пустой строкой на
  каждое заявление: человек не видел, что подставит справочник, и вписывал
  наименование руками - то есть запасной путь становился основным, а справочник,
  ради которого заводились дома, не работал.

  Почему не `Select.jsx`. Тот выбирает из закрытого перечня (стадия, роль,
  канал), и вписать в него своё нельзя. Участок - случай другой: подсудность
  бывает нетиповой, и наименование, которого нет в справочнике, обязано
  вводиться прямо здесь, без правки справочника ради одного заявления (решение
  владельца 11.09.2026). Поэтому разметка - поле ввода, а список к нему
  подсказка.

  Список и стрелка - те же, что у `Select.jsx` (общий `useReveal.js`, общая
  стрелка и галочка): два выпадающих списка кабинета обязаны открываться
  одинаково.
*/

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { Notice } from './Common.jsx'
import { Arrow, Check } from './Select.jsx'
import useReveal from './useReveal.js'

/** Подходит ли участок под набранное: ищем и по номеру, и по наименованию. */
function matching(sections, query) {
  if (!query) return sections
  return sections.filter((section) =>
    `${section.number} ${section.name}`.toLowerCase().includes(query),
  )
}

export default function CourtField({
  label,
  value,
  onChange,
  // Пустой список - обычное состояние, а не сбой: участки ещё грузятся, их не
  // завели вовсе или у человека нет права на справочник. Поле при этом
  // работает: участок вписывается руками, а список объясняет, почему он пуст.
  sections = [],
  error = null,
  placeholder,
  required = false,
  /*
    Ширина как у адреса, а не как у имени: наименование участка - это шапка
    заявления («Мировому судье судебного участка № 6 Заводского района
    г. Кемерово»), и в поле на 380 пикселей от него видно «Мировому судье
    судебно…» - ровно до номера, которым участки и различаются (осмотр
    14.09.2026). В строке с кнопкой `.field--l` занимает остаток строки.
  */
  width = 'l',
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const root = useRef(null)
  const field = useRef(null)
  const listId = useId()
  const fieldId = useId()
  const { mounted, shown } = useReveal(open)

  /*
    Набранное фильтрует список, но ТОЧНОЕ совпадение - нет. Иначе, выбрав
    участок, человек видел бы список из одной строки - той самой, что уже
    выбрана, - и сменить выбор мог бы только стерев поле.
  */
  const chosen = sections.some((section) => section.name === value)
  const matches = useMemo(
    () => (chosen ? sections : matching(sections, value.trim().toLowerCase())),
    [chosen, sections, value],
  )

  // Нажатие мимо поля закрывает список - как у остальных списков кабинета.
  useEffect(() => {
    if (!open) return undefined
    const outside = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [open])

  const choose = useCallback(
    (index) => {
      const section = matches[index]
      if (!section) return
      onChange(section.name)
      setOpen(false)
      field.current?.focus()
    },
    [matches, onChange],
  )

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        // Список закрыт - Esc не наш, пусть закрывает окно вокруг поля.
        if (!open) return
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        return
      }
      if (event.key === 'Tab') {
        setOpen(false)
        return
      }
      /*
        Enter выбирает подсвеченную строку и НЕ отправляет форму: поле участка в
        «Исполнении» стоит внутри формы, и отправка вместо выбора сохранила бы
        то, что человек только что набирал, вместо того, что он выбирал.
        Подсвеченной строки нет - Enter работает как обычно.
      */
      if (event.key === 'Enter') {
        if (!open || active < 0) return
        event.preventDefault()
        choose(active)
        return
      }
      const moves = {
        ArrowDown: () => Math.min(active + 1, matches.length - 1),
        ArrowUp: () => Math.max(active - 1, 0),
        Home: () => 0,
        End: () => matches.length - 1,
      }
      const move = moves[event.key]
      if (!move) return
      event.preventDefault()
      setOpen(true)
      setActive(open ? move() : 0)
    },
    [active, choose, matches.length, open],
  )

  /*
    🔴 Список лежит СНАРУЖИ подписи, а связь с полем задана через `htmlFor`.

    Внутри `<label>` весь текст становится названием поля для читалки экрана - то
    же правило, по которому в `Common.jsx` вынесено пояснение. С раскрытым
    списком поле называлось бы «Судебный участок Мировому судье судебного
    участка № 5… Мировому судье судебного участка № 6…», и человек, идущий по
    форме голосом, слушал бы весь справочник вместо названия. Поймано прогоном
    14.09.2026: `getByLabelText('Судебный участок')` перестаёт находить поле
    ровно после открытия списка - и перестаёт ровно потому же, почему его
    перестала бы находить читалка.
  */
  return (
    <div className={`field${width ? ` field--${width}` : ''}`}>
      <label className="field__label" htmlFor={fieldId}>
        {label}
      </label>
      <span className="select" ref={root}>
        <input
          ref={field}
          id={fieldId}
          className="field__input select__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          value={value}
          placeholder={placeholder}
          required={required}
          onChange={(event) => {
            onChange(event.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <Arrow open={open} />

        {mounted ? (
          <Options
            listId={listId}
            shown={shown}
            matches={matches}
            value={value}
            active={active}
            onHover={setActive}
            onChoose={choose}
          />
        ) : null}
      </span>
      {/* Ошибка загрузки списка стоит под своим полем, а не наверху страницы:
          вписать участок руками по-прежнему можно, и человек должен видеть,
          почему выбирать не из чего (правило 3 скилла `frontend`). */}
      {error ? <Notice error={error} /> : null}
    </div>
  )
}

/** Список подсказок - или объяснение, почему подсказок нет. */
function Options({ listId, shown, matches, value, active, onHover, onChoose }) {
  const classes = `select__list${shown ? ' select__list--shown' : ''}`

  if (matches.length === 0) {
    return (
      <div className={`${classes} select__empty`} role="status">
        {value.trim()
          ? 'Совпадений нет - участок останется таким, как вы его вписали.'
          : 'Участки не заведены. «Настройки» → «Судебные участки», иначе участок придётся вписывать руками каждый раз.'}
      </div>
    )
  }

  return (
    <ul id={listId} role="listbox" tabIndex={-1} className={classes}>
      {matches.map((section, index) => (
        <li
          key={section.id}
          id={`${listId}-${index}`}
          role="option"
          aria-selected={section.name === value}
          onMouseEnter={() => onHover(index)}
          /*
            `preventDefault` на нажатии удерживает фокус в поле: без него
            нажатие на строку уводит фокус с поля, и выбор происходил бы уже
            после того, как поле его потеряло.
          */
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onChoose(index)}
          className={`select__option${index === active ? ' select__option--active' : ''}`}
        >
          <Check shown={section.name === value} />
          <span className="select__label select__label--wrap">{section.name}</span>
        </li>
      ))}
    </ul>
  )
}
